import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { IMAGES, scrollToId } from "@/data";
import { resolveSrc } from "@/components/Gallery";

const API = process.env.REACT_APP_BACKEND_URL;

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 },
  }),
};

export default function Bio() {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/members`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setMembers)
      .catch(() => setMembers([]));
  }, []);
  return (
    <section
      id="band"
      data-testid="band-bio-section"
      className="relative border-t border-neutral-900 bg-[#0A0A0A]"
    >
      <div className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
          <div>
            <motion.p
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="font-mono-tac text-xs uppercase tracking-[0.35em] text-red-600"
            >
              04 — La band
            </motion.p>
            <motion.h2
              variants={reveal}
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="font-display mt-4 text-5xl uppercase leading-none text-neutral-100 sm:text-7xl"
            >
              Rumore
              <br />
              <span className="text-outline-red">Vero</span>
            </motion.h2>
            <motion.div
              variants={reveal}
              custom={2}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              data-testid="band-bio-description"
              className="mt-8 max-w-lg space-y-5 text-base leading-relaxed text-neutral-400"
            >
              <p>
                I Linea Cremisi hanno un suono nato tra sale prova e palchi
                improvvisati, con una sola regola — niente filtri. Sporco
                fuori, sincero dentro.
              </p>
              <p>
                Distorsione, melodia e testi che parlano di attese e di cieli
                rossi. Il nuovo EP è la loro fotografia più nitida: sei brani,
                zero compromessi.
              </p>
            </motion.div>

            <motion.blockquote
              variants={reveal}
              custom={3}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              data-testid="band-manifesto"
              className="font-lyric mt-10 max-w-lg border-l-2 border-[#C8102E] bg-red-950/10 p-6 text-xl italic leading-relaxed text-neutral-300"
            >
              Rock italiano con anima grunge.
              <br />
              Chitarre sporche, parole vere, visione lucida.
              <br />
              <span className="text-[#FF2A4B]">
                Suoniamo quello che non si può ignorare.
              </span>
            </motion.blockquote>

            <div className="mt-12 grid grid-cols-2 gap-3">
              {members.map((m, i) => (
                <motion.div
                  key={m.id}
                  variants={reveal}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  data-testid={`band-member-${m.tag}`}
                  className="group border border-neutral-800 bg-[#0D0D0D] transition-colors duration-300 hover:border-red-700"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={resolveSrc(m.photo)}
                      alt={`${m.name} — ${m.role}`}
                      className="h-full w-full object-cover object-[50%_28%] grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                  </div>
                  <div className="p-4">
                    <span className="font-serif-logo block text-sm font-bold tracking-wide text-neutral-100">
                      {m.name}
                    </span>
                    <span className="font-mono-tac mt-1.5 block text-[10px] uppercase tracking-[0.25em] text-red-600">
                      {m.role}
                    </span>
                    {m.badge && (
                      <button
                        data-testid={`band-member-${m.tag}-badge`}
                        onClick={() => scrollToId("endorsement")}
                        className="mt-3 inline-flex items-center gap-1.5 border border-neutral-800 px-2 py-1 font-mono-tac text-[9px] uppercase tracking-[0.2em] text-neutral-400 transition-colors duration-200 hover:border-red-700 hover:text-red-500"
                      >
                        <BadgeCheck className="h-3 w-3 text-red-600" />
                        {m.badge}
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 grid-rows-2 gap-3">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="group relative row-span-2 overflow-hidden border border-neutral-800"
            >
              <img
                src={IMAGES.vocalist}
                alt="Giuseppe Cozzolino dal vivo, chitarra e microfono"
                className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <span className="font-mono-tac absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.3em] text-neutral-300">
                Dal vivo, sul mare
              </span>
            </motion.div>
            <motion.div
              variants={reveal}
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="group relative overflow-hidden border border-neutral-800"
            >
              <img
                src={IMAGES.guitarist}
                alt="Sossio Aversana alla chitarra solista"
                className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </motion.div>
            <motion.div
              variants={reveal}
              custom={2}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="group relative overflow-hidden border border-neutral-800"
            >
              <img
                src={IMAGES.bassist}
                alt="Jacopo Colombo al basso"
                className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
